import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  InputAdornment,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Autocomplete
} from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

const STORE_LOCATIONS = [
  'Ahmedabad HO',
  'Dalhousie Base Camp',
  'Haridwar Camper Store',
  'Kasol Base Camp',
  'Lohajung Base Camp',
  'Manali Base Camp',
  'Polo Forest Campsite',
  'Sankri Base Camp',
  'Saputara Camper Store'
];

export default function StoreStock() {
  const [selectedLocation, setSelectedLocation] = useState(STORE_LOCATIONS[0]);
  const [variants, setVariants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [storeTypeFilter, setStoreTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');

  // Local editing state for quantities per variant
  const [stockEdits, setStockEdits] = useState({});

  // Transfer Dialog State
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({
    variantId: '',
    fromLocation: STORE_LOCATIONS[0],
    toLocation: STORE_LOCATIONS[1],
    quantity: 10,
    remarks: ''
  });
  const [transferLoading, setTransferLoading] = useState(false);

  // Add Stock Dialog State
  const [addStockOpen, setAddStockOpen] = useState(false);
  const [addStockForm, setAddStockForm] = useState({
    variantId: '',
    locationName: STORE_LOCATIONS[0],
    quantityToAdd: 10,
    remarks: ''
  });
  const [addStockLoading, setAddStockLoading] = useState(false);

  const fetchStock = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const params = new URLSearchParams();
      if (storeTypeFilter) params.append('storeType', storeTypeFilter);
      if (categoryFilter) params.append('categoryId', categoryFilter);
      if (search) params.append('search', search);

      const [sRes, cRes] = await Promise.all([
        axios.get(`/api/v1/admin/store/stock?${params.toString()}`, { headers }),
        axios.get('/api/v1/admin/store/categories', { headers })
      ]);

      if (sRes.data.success) {
        setVariants(sRes.data.data);
        const edits = {};
        sRes.data.data.forEach((v) => {
          // Find stock for selected location if recorded, otherwise 0
          const locObj = v.inventoryLocations?.find((l) => l.locationName === selectedLocation);
          edits[v.id] = locObj ? (locObj.totalQuantity ?? locObj.availableQuantity ?? 0) : 0;
        });
        setStockEdits(edits);
      }
      if (cRes.data.success) setCategories(cRes.data.data);
    } catch (err) {
      setError('Failed to load stock data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStock();
  }, [selectedLocation, storeTypeFilter, categoryFilter]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchStock();
  };

  const handleSaveStock = async (variantId) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const newQty = stockEdits[variantId];

      await axios.patch(`/api/v1/admin/store/stock/variants/${variantId}`, {
        stockQuantity: newQty,
        locationName: selectedLocation
      }, { headers });

      setSuccessMsg(`Stock updated successfully for ${selectedLocation}!`);
      setTimeout(() => setSuccessMsg(''), 3000);
      fetchStock();
    } catch (err) {
      alert('Failed to update stock');
    }
  };

  const handleOpenTransfer = (defaultVariant = null) => {
    setTransferForm({
      variantId: defaultVariant ? defaultVariant.id : (variants[0]?.id || ''),
      fromLocation: selectedLocation,
      toLocation: STORE_LOCATIONS.find((l) => l !== selectedLocation) || STORE_LOCATIONS[1],
      quantity: 10,
      remarks: ''
    });
    setTransferOpen(true);
  };

  const handleExecuteTransfer = async () => {
    try {
      setTransferLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.post('/api/v1/admin/store/stock/transfer', transferForm, { headers });
      if (res.data.success) {
        setSuccessMsg(res.data.message);
        setTimeout(() => setSuccessMsg(''), 4000);
        setTransferOpen(false);
        fetchStock();
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to transfer stock');
    } finally {
      setTransferLoading(false);
    }
  };

  // Add / Inward Stock Handler
  const handleOpenAddStock = (defaultVariant = null) => {
    setAddStockForm({
      variantId: defaultVariant ? defaultVariant.id : (variants[0]?.id || ''),
      locationName: selectedLocation,
      quantityToAdd: 10,
      remarks: ''
    });
    setAddStockOpen(true);
  };

  const handleExecuteAddStock = async () => {
    try {
      if (!addStockForm.variantId) {
        alert('Please select a product variant');
        return;
      }
      if (Number(addStockForm.quantityToAdd) <= 0) {
        alert('Quantity must be greater than 0');
        return;
      }

      setAddStockLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Find current quantity for selected variant and location
      const targetVariant = variants.find((v) => String(v.id) === String(addStockForm.variantId));
      const locObj = targetVariant?.inventoryLocations?.find((l) => l.locationName === addStockForm.locationName);
      const currentQty = locObj ? (locObj.totalQuantity ?? locObj.availableQuantity ?? 0) : 0;
      const newTotalQty = currentQty + Number(addStockForm.quantityToAdd);

      await axios.patch(`/api/v1/admin/store/stock/variants/${addStockForm.variantId}`, {
        stockQuantity: newTotalQty,
        locationName: addStockForm.locationName
      }, { headers });

      setSuccessMsg(`Added +${addStockForm.quantityToAdd} units to ${addStockForm.locationName} successfully!`);
      setTimeout(() => setSuccessMsg(''), 4000);
      setAddStockOpen(false);
      fetchStock();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to add stock');
    } finally {
      setAddStockLoading(false);
    }
  };

  const getLocationStock = (variant) => {
    const locObj = variant.inventoryLocations?.find((l) => l.locationName === selectedLocation);
    if (locObj) {
      return {
        total: locObj.totalQuantity ?? 0,
        available: locObj.availableQuantity ?? 0,
        reserved: locObj.reservedQuantity ?? 0,
        rented: locObj.rentedQuantity ?? 0
      };
    }
    return {
      total: 0,
      available: 0,
      reserved: 0,
      rented: 0
    };
  };

  // Only display products / variants that have available stock (> 0) at the selected location
  const activeStockVariants = variants.filter((v) => {
    const locStock = getLocationStock(v);
    return locStock.total > 0;
  });

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      {/* Top Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Inventory2Icon color="primary" sx={{ fontSize: 34 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">Stock & Inventory Management</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage live available stock and transfer inventory across Head Office, Transit Hubs, and Campsites.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => handleOpenAddStock()}
            sx={{ borderRadius: 2, px: 2.5 }}
          >
            Add Stock
          </Button>

          <Button
            variant="contained"
            color="secondary"
            startIcon={<CompareArrowsIcon />}
            onClick={() => handleOpenTransfer()}
            sx={{ borderRadius: 2, px: 2.5 }}
          >
            Transfer Stock Between Stores
          </Button>
        </Box>
      </Box>

      {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* 9 Location Selector Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }} elevation={1}>
        <Tabs
          value={selectedLocation}
          onChange={(e, val) => setSelectedLocation(val)}
          variant="scrollable"
          scrollButtons="auto"
          textColor="primary"
          indicatorColor="primary"
          sx={{ px: 1 }}
        >
          {STORE_LOCATIONS.map((loc) => (
            <Tab
              key={loc}
              value={loc}
              icon={<LocationOnIcon fontSize="small" />}
              iconPosition="start"
              label={loc}
              sx={{ fontWeight: 600, py: 1.5 }}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Filters Bar */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', borderRadius: 2 }}>
        <TextField
          select
          size="small"
          label="Store Section"
          value={storeTypeFilter}
          onChange={(e) => setStoreTypeFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All Sections</MenuItem>
          <MenuItem value="merchandise">Merchandise Only</MenuItem>
          <MenuItem value="camper_store">Camper's Store Only</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          label="Category"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All Categories</MenuItem>
          {categories.map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
          ))}
        </TextField>

        <Box component="form" onSubmit={handleSearchSubmit} sx={{ display: 'flex', gap: 1, flex: 1, minWidth: 260 }}>
          <TextField
            size="small"
            fullWidth
            placeholder={`Search product or SKU in ${selectedLocation}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              )
            }}
          />
          <Button type="submit" variant="contained" sx={{ borderRadius: 1.5 }}>
            Search
          </Button>
        </Box>
      </Paper>

      {/* Stock Table (Filtered to Available Products Only) */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ bgcolor: '#F8FAFC' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Product Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Variant / Size</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Current Location</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Price / Rent</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Stock Breakdown</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 140 }}>Update Stock</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activeStockVariants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                      <Inventory2Icon sx={{ fontSize: 44, color: '#94A3B8' }} />
                      <Typography variant="subtitle1" fontWeight="bold">
                        No stocked items currently at {selectedLocation}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Click the "Add Stock" button above to allocate initial inventory to this location.
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => handleOpenAddStock()}
                        sx={{ mt: 1, borderRadius: 2 }}
                      >
                        Add Stock to {selectedLocation}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                activeStockVariants.map((v) => {
                  const locStock = getLocationStock(v);
                  const isModified = String(stockEdits[v.id]) !== String(locStock.total);

                  return (
                    <TableRow key={v.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <img
                            src={v.product?.images?.[0] || 'https://images.unsplash.com/photo-1521572267360-ee0c2909d518?w=100'}
                            alt={v.product?.name}
                            style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', background: '#F1F5F9' }}
                          />
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">{v.product?.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{v.product?.storeType === 'camper_store' ? "Camper's Store" : 'Merchandise'}</Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>{v.product?.category?.name || 'General'}</TableCell>

                      <TableCell>
                        <Chip label={v.size || v.color || 'Standard'} size="small" sx={{ fontWeight: 600 }} />
                      </TableCell>

                      <TableCell>
                        <Chip
                          icon={<LocationOnIcon fontSize="small" />}
                          label={selectedLocation}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>

                      <TableCell>
                        {v.product?.saleType === 'rental' ? (
                          <Typography variant="body2" fontWeight="bold">₹{v.rentalPrice || v.product?.baseRentalPrice || 0} / trek</Typography>
                        ) : v.product?.saleType === 'purchase' ? (
                          <Typography variant="body2" fontWeight="bold">₹{v.purchasePrice || v.product?.basePurchasePrice || 0}</Typography>
                        ) : (
                          <Box>
                            <Typography variant="caption" display="block">Buy: ₹{v.purchasePrice || v.product?.basePurchasePrice || 0}</Typography>
                            <Typography variant="caption" display="block">Rent: ₹{v.rentalPrice || v.product?.baseRentalPrice || 0}</Typography>
                          </Box>
                        )}
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Chip
                            label={`Available: ${locStock.available}`}
                            size="small"
                            color={locStock.available > 0 ? 'success' : 'error'}
                            sx={{ fontWeight: 700 }}
                          />
                          {locStock.rented > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Rented with campers: {locStock.rented}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={stockEdits[v.id] ?? ''}
                          onChange={(e) => setStockEdits({ ...stockEdits, [v.id]: e.target.value })}
                          inputProps={{ min: 0, style: { padding: '6px 10px' } }}
                          sx={{ width: 90 }}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            variant="contained"
                            size="small"
                            color={isModified ? 'primary' : 'inherit'}
                            startIcon={<SaveIcon />}
                            onClick={() => handleSaveStock(v.id)}
                            disabled={!isModified}
                          >
                            Save
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="secondary"
                            onClick={() => handleOpenTransfer(v)}
                          >
                            Transfer
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dynamic Add Stock Dialog */}
      <Dialog open={addStockOpen} onClose={() => setAddStockOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Add Stock to Inventory
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            <TextField
              select
              label="Select Location *"
              fullWidth
              value={addStockForm.locationName}
              onChange={(e) => setAddStockForm({ ...addStockForm, locationName: e.target.value })}
            >
              {STORE_LOCATIONS.map((loc) => (
                <MenuItem key={loc} value={loc}>{loc}</MenuItem>
              ))}
            </TextField>

            <Autocomplete
              options={variants}
              getOptionLabel={(v) => `${v.product?.name || 'Product'} — ${v.size || v.color || 'Standard'} (${v.product?.storeType === 'camper_store' ? "Camper's Gear" : 'Merchandise'})`}
              value={variants.find((v) => String(v.id) === String(addStockForm.variantId)) || null}
              isOptionEqualToValue={(option, val) => String(option.id) === String(val.id)}
              onChange={(_, newVal) => {
                setAddStockForm({ ...addStockForm, variantId: newVal ? newVal.id : '' });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search & Select Product Variant *"
                  placeholder="Type product name or size..."
                />
              )}
            />

            {addStockForm.variantId && (
              <Box sx={{ bgcolor: '#F8FAFC', p: 2, borderRadius: 1.5, border: '1px solid #E2E8F0' }}>
                {(() => {
                  const targetV = variants.find((v) => String(v.id) === String(addStockForm.variantId));
                  const locStock = targetV?.inventoryLocations?.find((l) => l.locationName === addStockForm.locationName);
                  const currentUnits = locStock ? (locStock.totalQuantity ?? locStock.availableQuantity ?? 0) : 0;
                  return (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Current stock at <strong>{addStockForm.locationName}</strong>:
                      </Typography>
                      <Chip label={`${currentUnits} Units`} size="small" color={currentUnits > 0 ? 'success' : 'default'} />
                    </Box>
                  );
                })()}
              </Box>
            )}

            <TextField
              label="Quantity to Inward / Add (Units) *"
              type="number"
              fullWidth
              value={addStockForm.quantityToAdd}
              onChange={(e) => setAddStockForm({ ...addStockForm, quantityToAdd: e.target.value })}
              inputProps={{ min: 1 }}
              helperText="This quantity will be added to the location's current available stock"
            />

            <TextField
              label="Stock Batch / Supplier / PO Reference"
              fullWidth
              multiline
              rows={2}
              placeholder="e.g. PO-8492 new procurement from Quechua / Trekker Supply"
              value={addStockForm.remarks}
              onChange={(e) => setAddStockForm({ ...addStockForm, remarks: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddStockOpen(false)} disabled={addStockLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExecuteAddStock}
            disabled={addStockLoading}
            startIcon={addStockLoading ? <CircularProgress size={16} /> : <AddCircleOutlineIcon />}
          >
            {addStockLoading ? 'Adding Stock...' : 'Confirm & Add Stock'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inter-Location Stock Transfer Dialog */}
      <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Inter-Store Stock Transfer
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            <Autocomplete
              options={variants}
              getOptionLabel={(v) => `${v.product?.name || 'Product'} — ${v.size || v.color || 'Standard'}`}
              value={variants.find((v) => String(v.id) === String(transferForm.variantId)) || null}
              isOptionEqualToValue={(option, val) => String(option.id) === String(val.id)}
              onChange={(_, newVal) => {
                setTransferForm({ ...transferForm, variantId: newVal ? newVal.id : '' });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search & Select Product Variant *"
                  placeholder="Type product name or size..."
                />
              )}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Transfer From (Source Location) *"
                  fullWidth
                  value={transferForm.fromLocation}
                  onChange={(e) => setTransferForm({ ...transferForm, fromLocation: e.target.value })}
                >
                  {STORE_LOCATIONS.map((loc) => (
                    <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Transfer To (Destination Location) *"
                  fullWidth
                  value={transferForm.toLocation}
                  onChange={(e) => setTransferForm({ ...transferForm, toLocation: e.target.value })}
                >
                  {STORE_LOCATIONS.filter((l) => l !== transferForm.fromLocation).map((loc) => (
                    <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <TextField
              label="Transfer Quantity (Units) *"
              type="number"
              fullWidth
              value={transferForm.quantity}
              onChange={(e) => setTransferForm({ ...transferForm, quantity: e.target.value })}
              inputProps={{ min: 1 }}
            />

            <TextField
              label="Transfer Reason / Dispatch Notes"
              fullWidth
              multiline
              rows={2}
              placeholder="e.g. Seasonal dispatch before winter trek batches start"
              value={transferForm.remarks}
              onChange={(e) => setTransferForm({ ...transferForm, remarks: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTransferOpen(false)} disabled={transferLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExecuteTransfer}
            disabled={transferLoading}
            startIcon={transferLoading ? <CircularProgress size={16} /> : <CompareArrowsIcon />}
          >
            {transferLoading ? 'Transferring...' : 'Execute Stock Transfer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
